import { z } from 'zod';

import type { Booking, Slots } from '@/features/scheduling';
import { bookingPageSchema, bookingSchema, slotsSchema } from '@/features/scheduling';
import type { Fetcher } from '@/lib/api/fetcher';

import type {
  OtpRequest,
  OtpVerify,
  PublicAddon,
  PublicBooking,
  PublicService,
  PublicStaff,
} from './schemas';
import {
  otpRequestSchema,
  otpVerifySchema,
  publicAddonSchema,
  publicBookingSchema,
  publicServiceSchema,
  publicStaffSchema,
} from './schemas';

/**
 * The §5 routes the client PWA calls, and only those.
 *
 * The booking journey is anonymous until the OTP step, so the two `/auth/client/otp/*`
 * routes come first; everything after them rides the cookie the verify sets. There is no
 * route here that could return another client's data — the api scopes `/bookings` to the
 * caller (R14) and `/public/bookings/{ref}` to the signed link.
 */

/** One item of `POST /bookings` from the PWA. */
export interface ClientBookingItem {
  service_id: string;
  staff_id: string;
  start_at: string;
  addon_ids?: string[];
  /**
   * BP-21 §11 item 6: the shade she tried on and wants.
   *
   * On the item and not the booking: a client who books a manicure and a
   * pedicure in one go may have tried a shade for only one of them.
   */
  preselected_sku_id?: string;
}

/** What the client app can ask the api to do. */
export interface BookingApi {
  /** `GET /public/services` — the menu, before anyone has verified a number. */
  listServices: () => Promise<PublicService[]>;
  /** `GET /public/addons`. */
  listAddons: () => Promise<PublicAddon[]>;
  /** `GET /public/staff` — the technicians taking online bookings. */
  listStaff: () => Promise<PublicStaff[]>;
  availability: (query: {
    date: string;
    service_ids: string[];
    staff_id?: string;
    addon_ids?: string[];
  }) => Promise<Slots>;
  requestOtp: (body: { phone_e164: string; consent_reminders: boolean }) => Promise<OtpRequest>;
  verifyOtp: (body: { phone_e164: string; code: string }) => Promise<OtpVerify>;
  createBooking: (
    body: { items: ClientBookingItem[]; source: string; client_id?: string | null },
    idempotencyKey: string,
  ) => Promise<Booking>;
  myBookings: (query?: { from?: string }) => Promise<{ items: Booking[]; next_cursor: string | null }>;
  readBooking: (id: string) => Promise<Booking>;
  publicBooking: (ref: string, token: string) => Promise<PublicBooking>;
  reschedule: (
    id: string,
    items: ClientBookingItem[],
    idempotencyKey: string,
  ) => Promise<Booking>;
  cancel: (id: string, reason: string, idempotencyKey: string) => Promise<Booking>;
}

/** Build a query string, leaving absent filters out and repeating a repeated one. */
export function bookingQueryString(
  query: Record<string, string | string[] | undefined>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) {
      continue;
    }
    if (Array.isArray(value)) {
      for (const entry of value) {
        search.append(key, entry);
      }
      continue;
    }
    if (value !== '') {
      search.set(key, value);
    }
  }
  const text = search.toString();
  return text === '' ? '' : `?${text}`;
}

/** Bind the client journey's routes to a transport. */
export function createBookingApi(fetcher: Fetcher): BookingApi {
  const idem = (key: string): Record<string, string> => ({ 'Idempotency-Key': key });
  return {
    listServices: () =>
      fetcher.json('/v1/public/services', { method: 'GET' }, z.array(publicServiceSchema)),
    listAddons: () =>
      fetcher.json('/v1/public/addons', { method: 'GET' }, z.array(publicAddonSchema)),
    listStaff: () =>
      fetcher.json('/v1/public/staff', { method: 'GET' }, z.array(publicStaffSchema)),
    availability: (query) =>
      fetcher.json(
        `/v1/public/availability${bookingQueryString({
          date: query.date,
          service_ids: query.service_ids,
          staff_id: query.staff_id,
          addon_ids: query.addon_ids,
        })}`,
        { method: 'GET' },
        slotsSchema,
      ),
    requestOtp: (body) =>
      fetcher.json('/v1/auth/client/otp/request', { method: 'POST', body }, otpRequestSchema),
    verifyOtp: (body) =>
      fetcher.json('/v1/auth/client/otp/verify', { method: 'POST', body }, otpVerifySchema),
    createBooking: (body, key) =>
      fetcher.json('/v1/bookings', { method: 'POST', body, headers: idem(key) }, bookingSchema),
    myBookings: (query = {}) =>
      fetcher.json(
        `/v1/bookings${bookingQueryString({ ...query })}`,
        { method: 'GET' },
        bookingPageSchema,
      ),
    readBooking: (id) => fetcher.json(`/v1/bookings/${id}`, { method: 'GET' }, bookingSchema),
    publicBooking: (ref, token) =>
      fetcher.json(
        `/v1/public/bookings/${ref}${bookingQueryString({ token })}`,
        { method: 'GET' },
        publicBookingSchema,
      ),
    reschedule: (id, items, key) =>
      fetcher.json(
        `/v1/bookings/${id}/reschedule`,
        { method: 'POST', body: { items }, headers: idem(key) },
        bookingSchema,
      ),
    cancel: (id, reason, key) =>
      fetcher.json(
        `/v1/bookings/${id}/transition`,
        { method: 'POST', body: { to: 'cancelled', reason }, headers: idem(key) },
        bookingSchema,
      ),
  };
}

/** The shape `z` gives a booking page; re-exported so a caller need not import Zod. */
export type BookingPageShape = z.infer<typeof bookingPageSchema>;
